<script setup lang="ts">
import { z } from 'zod'
import type { FormSubmitEvent } from '#ui/types'

const auth = useAuth()

definePageMeta({
  layout: 'auth',
  path: '/forgot-password',
})

const loading = ref(false)
const success = ref(false)
const errorMsg = ref('')

const schema = z.object({
  email: z.string().email('Invalid email address'),
})

type Schema = z.output<typeof schema>

const state = reactive({
  email: '',
})

const handleForgot = async (event: FormSubmitEvent<Schema>) => {
  loading.value = true
  errorMsg.value = ''
  try {
    await (auth as any).forgetPassword(
      {
        email: event.data.email,
        redirectTo: ROUTES.RESET_PASSWORD,
      },
      {
        onSuccess: () => {
          success.value = true
        },
        onError: (ctx: any) => {
          console.error('Forgot password error:', ctx.error)
          errorMsg.value =
            ctx.error.message || 'Failed to send reset link. Please check your email and try again.'
        },
      },
    )
  } catch (error: any) {
    console.error(error)
    errorMsg.value = 'An unexpected error occurred. Please try again later.'
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-6 w-full max-w-sm mx-auto">
    <div class="text-center mb-2">
      <div
        class="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary-100 text-primary-600 mb-4 dark:bg-primary-900/30 dark:text-primary-400"
      >
        <UIcon name="i-heroicons-key" class="w-6 h-6" />
      </div>
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white mb-1">Forgot Password?</h1>
      <p class="text-sm text-gray-500 dark:text-gray-400">
        No worries! Enter your email and we'll send you reset instructions.
      </p>
    </div>

    <!-- Success State -->
    <div
      v-if="success"
      class="flex flex-col items-center justify-center py-8 px-6 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-3xl text-center shadow-sm"
    >
      <div
        class="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center text-green-600 dark:text-green-400 mb-5 shadow-inner"
      >
        <UIcon name="i-heroicons-check-circle" class="w-10 h-10" />
      </div>
      <h3 class="text-xl font-bold text-green-900 dark:text-green-100 mb-3">Check your email</h3>
      <p class="text-sm text-green-700 dark:text-green-300 max-w-[280px] leading-relaxed">
        We've sent a password reset link to <span class="font-bold">{{ state.email }}</span
        >.
      </p>
      <UButton
        :to="ROUTES.LOGIN"
        variant="link"
        color="success"
        class="mt-6 font-bold text-base"
        icon="i-heroicons-arrow-left"
      >
        Back to Login
      </UButton>
    </div>

    <!-- Form State -->
    <div v-else class="flex flex-col gap-5">
      <UAlert
        v-if="errorMsg"
        color="error"
        variant="soft"
        icon="i-heroicons-exclamation-triangle"
        :title="errorMsg"
      />

      <UForm :schema="schema" :state="state" @submit="handleForgot" class="flex flex-col gap-5">
        <UFormField label="Email Address" name="email" class="font-medium">
          <UInput
            v-model="state.email"
            type="email"
            placeholder="you@example.com"
            size="xl"
            icon="i-heroicons-envelope"
            class="w-full shadow-sm"
          />
        </UFormField>

        <UButton
          type="submit"
          block
          size="xl"
          color="primary"
          class="font-bold shadow-lg shadow-primary-500/20 mt-2 hover:scale-[1.02] active:scale-[0.98] transition-all"
          :loading="loading"
        >
          Send Reset Link
        </UButton>

        <div class="text-center">
          <UButton
            :to="ROUTES.LOGIN"
            variant="link"
            color="neutral"
            icon="i-heroicons-arrow-left"
            class="font-medium text-sm"
          >
            Back to Login
          </UButton>
        </div>
      </UForm>
    </div>
  </div>
</template>
